'use client'
import { usePathname } from 'next/navigation'
import InlineLinkList from '@/components/common/InlineLinkList'
import {
  getCarePackageCategoryUrl,
  getCarePackageNonCityCategoryUrl,
  getServiceableCitySlugFromPathname,
  getDiseaseCategoriesProductListUrl,
  getDiseaseCategoriesUrl,
  getTitleCaseNameBySlug,
} from '@/utils/commonFunc'

type CityLinkVariant = 'carePackage' | 'diseaseCategory'

interface CityAwareLinkListProps {
  id?: string
  heading: string
  names: string[]
  variant: CityLinkVariant
  serviceableCitySlugs: string[]
  // Base label of a link prepended before the names; " in {city}" is appended on city routes,
  // and the href is built here from the resolved city.
  // A plain string, not a builder function: this is a Client Component, so props must serialize.
  leadingLinkLabel?: string
}

const buildItem = (
  name: string,
  variant: CityLinkVariant,
  citySlug?: string,
  cityName?: string
) => {
  if (variant === 'carePackage') {
    return {
      label: citySlug && cityName ? `${name} in ${cityName}` : name,
      href: citySlug
        ? getCarePackageCategoryUrl(citySlug, name)
        : getCarePackageNonCityCategoryUrl(name),
    }
  }

  return {
    label:
      citySlug && cityName ? `${name} test in ${cityName}` : `${name} test`,
    href: getDiseaseCategoriesProductListUrl(citySlug, name),
  }
}

// City comes from the live route so the location modal updates these links; the root layout this sits in is not re-rendered on navigation.
const CityAwareLinkList = ({
  id,
  heading,
  names,
  variant,
  serviceableCitySlugs,
  leadingLinkLabel,
}: CityAwareLinkListProps) => {
  const citySlug =
    getServiceableCitySlugFromPathname(
      usePathname() || '',
      serviceableCitySlugs
    ) || undefined
  const cityName = citySlug ? getTitleCaseNameBySlug(citySlug) : undefined

  const items = names.map((name) =>
    buildItem(name, variant, citySlug, cityName)
  )

  return (
    <InlineLinkList
      id={id}
      heading={heading}
      items={
        leadingLinkLabel
          ? [
              {
                // Suffixed like the name items below, so the label matches the city the href points at.
                label: cityName
                  ? `${leadingLinkLabel} in ${cityName}`
                  : leadingLinkLabel,
                href: getDiseaseCategoriesUrl(citySlug),
              },
              ...items,
            ]
          : items
      }
    />
  )
}

export default CityAwareLinkList
